import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { ciInterface2 } from 'src/app/util/interfaces/ci';
import { LabelsInterface } from 'src/app/util/services/labels/data/label.inteface';
import { CmdbRegisterPageInconsistenciesService } from './data/cmdb-register-page-inconsistencies.service';
import { DateService } from 'src/app/services/date/date.service';
import { logChanges } from 'src/app/util/interfaces/log.interface';
import { KeyValuePipe } from '@angular/common';
import { MspButtonComponent } from 'src/app/page_component_item/msp-button/msp-button.component';
import { DotBounceLoadingEffectModule } from 'src/app/page_component_item/dot-bounce-loading-effect/dot-bounce-loading-effect.module';

@Component({
	selector: 'pcp-cmdb-register-page-inconsistencies',
	standalone: true,
	imports: [FontAwesomeModule, FormsModule, KeyValuePipe, MspButtonComponent, DotBounceLoadingEffectModule],
	templateUrl: './cmdb-register-page-inconsistencies.component.html',
	styleUrl: './cmdb-register-page-inconsistencies.component.scss',
})
export class CmdbRegisterPageInconsistenciesComponent implements OnInit, OnChanges {
	private readonly notificationService = inject(NotificationService);
	private readonly cmdbRegisterPageInconsistenciesService = inject(CmdbRegisterPageInconsistenciesService);
	protected readonly dateService = inject(DateService);
	icons = {
		faAngleUp: faAngleUp,
		faAngleDown: faAngleDown,
	};
	@Input() labels: LabelsInterface = {
		lbl_excecoes_inconsistencia_atualizada_sucesso: 'Inconsistency exceptions updated successfully!',
		lbl_erro_ao_salvar_excecao_de_inconsistencia: 'Error saving inconsistency exception',
		lbl_erro_ao_carregar_inconsistencias: 'Error loading inconsistencies',
		lbl_preencha_todos_asteriscos: 'Fill in at least the fields marked by *',
	};
	@Input() selectedCi: ciInterface2;
	@Input() viewType: 'read' | 'edit';

	collapseForm = {
		INCONSISTENCY_EXCEPTION: false,
		INCONSISTENCY_LOGS: false,
		INCONSISTENCY_LINKED: false,
	};

	inconsistenciesTypes = [];
	availableInconsistencyTypes: any[] = [];
	selectedInconsistenciesTypes: any[] = [];

	exceptionInconsistencyDate = '';
	exceptionInconsistencyNotes = '';

	inconsistenciesLogs = [];

	inconsistenciesExceptionLogs: logChanges[] = [];

	isLoadingLogs = false;
	isLoadingExceptionLogs = false;
	isLoadingRegisterForm = false;

	isSavingExceptions = false;
	private loadedCiId = '';

	ngOnInit(): void {
		this.loadSelectedCiData();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedCi']) this.loadSelectedCiData();
	}

	private loadSelectedCiData(): void {
		const ciId = this.selectedCi?.id?.toString().trim() || '';
		if (!ciId || ciId === this.loadedCiId) return;

		this.loadedCiId = ciId;
		this.getInconsistencies(ciId);
		this.getInconsistenciesExceptionLogs(ciId);
		this.getInconsistenciesType(ciId);
	}

	getInconsistencies(id: string): void {
		this.isLoadingLogs = true;
		this.cmdbRegisterPageInconsistenciesService.getInconsistencies(id).subscribe({
			next: (inconsistencies) => {
				this.inconsistenciesLogs = inconsistencies;
				this.isLoadingLogs = false;
			},
			error: (error) => {
				console.error('Error fetching inconsistencies:', error);
				this.notificationService.setNotificationMessage(this.labels.lbl_erro_ao_carregar_inconsistencias);
				this.isLoadingLogs = false;
			},
		});
	}

	getInconsistenciesExceptionLogs(id: string): void {
		this.isLoadingExceptionLogs = true;
		this.cmdbRegisterPageInconsistenciesService.getInconsistenciesExceptionLogs(id).subscribe({
			next: (logs) => {
				this.inconsistenciesExceptionLogs = logs;
				this.isLoadingExceptionLogs = false;
			},
			error: (error) => {
				console.error('Error fetching inconsistency exception logs:', error);
				this.isLoadingExceptionLogs = false;
			},
		});
	}

	getInconsistenciesType(idCi?: string): void {
		this.cmdbRegisterPageInconsistenciesService.getAllInconsistenciesTypes().subscribe({
			next: (types) => {
				this.inconsistenciesTypes = types;
				this.availableInconsistencyTypes = types.map((type) => ({
					...type,
					checked: false,
				}));

				// Limpa seleção anterior
				this.selectedInconsistenciesTypes = [];
				this.exceptionInconsistencyDate = '';
				this.exceptionInconsistencyNotes = '';

				// Se tiver um CI selecionado, busca exceções desse ativo
				if (idCi) {
					this.cmdbRegisterPageInconsistenciesService.getExceptionsInconsistencies(idCi).subscribe({
						next: (exceptions) => {
							if (!exceptions) {
								return;
							}

							// Backend retorna no formato { "640922": { "exceptions": { "4": "2026-03-04" }, "notes": "..." } }
							const cmdbData = exceptions[idCi];

							if (!cmdbData) {
								return;
							}

							const cmdbExceptions = cmdbData.exceptions || {};

							this.exceptionInconsistencyNotes = cmdbData.notes || '';

							// Marca os tipos que já possuem exceção e define a data (se existir)
							for (const type of this.availableInconsistencyTypes) {
								const typeIdKey = type.id == undefined ? '' : type.id.toString();

								if (typeIdKey && Object.prototype.hasOwnProperty.call(cmdbExceptions, typeIdKey)) {
									type.checked = true;
									this.selectedInconsistenciesTypes.push(type.id);

									const exceptionDate = cmdbExceptions[typeIdKey];
									if (exceptionDate && !this.exceptionInconsistencyDate) {
										this.exceptionInconsistencyDate = exceptionDate;
									}
								}
							}
						},
						error: (error) => {
							console.error('Error fetching inconsistency exceptions:', error);
						},
					});
				}
			},
			error: (error) => {
				console.error('Error fetching inconsistency types:', error);
			},
		});
	}

	onInconsistencySelected(inconsistencyType: any, checked: boolean): void {
		if (checked) {
			if (!this.selectedInconsistenciesTypes.includes(inconsistencyType.id)) {
				this.selectedInconsistenciesTypes.push(inconsistencyType.id);
			}
		} else {
			this.selectedInconsistenciesTypes = this.selectedInconsistenciesTypes.filter((id) => id !== inconsistencyType.id);
		}
	}

	areRequiredFieldsFulfilled(): boolean {
		if (this.selectedInconsistenciesTypes.length > 0 && !this.exceptionInconsistencyNotes.trim()) {
			this.notificationService.setNotificationMessage(this.labels.lbl_preencha_todos_asteriscos);
			return false;
		}
		return true;
	}

	saveExceptionsInconsistencies(): void {
		if (!this.areRequiredFieldsFulfilled()) return;
		if (this.isSavingExceptions) return;
		this.isSavingExceptions = true;
		this.cmdbRegisterPageInconsistenciesService
			.saveExceptionsInconsistencies(
				this.selectedCi.id,
				this.selectedInconsistenciesTypes,
				this.exceptionInconsistencyDate,
				this.exceptionInconsistencyNotes
			)
			.subscribe({
				next: () => {
					this.notificationService.setNotificationMessage(this.labels.lbl_excecoes_inconsistencia_atualizada_sucesso);
					this.getInconsistenciesExceptionLogs(this.selectedCi.id);
					this.isSavingExceptions = false;
				},
				error: (error) => {
					console.error('Error saving inconsistencies exceptions:', error);
					this.notificationService.setNotificationMessage(this.labels.lbl_erro_ao_salvar_excecao_de_inconsistencia);
					this.isSavingExceptions = false;
				},
			});
	}

	openInconsistency(id: string) {
		const url = `/myasset/inconsistency/${id}`;
		window.open(url, '_blank');
	}
}
